import { Controller } from "@hotwired/stimulus"
import { cropMatrix3d } from "../lib/homography"

// Consumer-side crop straightening (ADR-0082 crop-as-dewarp). A NON-axis-aligned
// crop quad cannot be rendered as a static server CSS string: the homography
// matrix3d is PIXEL-DEPENDENT (its perspective terms scale as 1/pixels, and the
// destination rect comes from getImgRect, which needs the rendered frame size and
// the image's natural dimensions). So the server emits the persisted quad as a
// data attribute and this controller solves the matrix3d client-side on connect
// and re-solves on every layout/resize via a ResizeObserver.
//
// The transform STRAIGHTENS and FRAMES in one move (ADR-0082): the destination
// rect IS the crop framing. Source points are the quad mapped into the frame's
// content-rect pixel space; destination is the frame content-rect corners
// (TL→TR→BR→BL). The matrix3d goes on the <img> with transform-origin 0 0 (the
// homography is in frame-relative pixel coords — origin MUST be 0 0).
//
// Single CROP toggle (default ON): with the controller connected the straightened
// transform is applied; `toggle(false)` clears it back to the plain image, and
// `toggle()` flips it.
//
// Markup (emitted by cropped_image_tag): a fixed-size overflow-hidden frame
// carrying data-controller="image-crop-homography" and the quad value, wrapping
// an <img> with data-image-crop-homography-target="image".
export default class extends Controller {
  static targets = ["image"]
  static values = {
    // [[x,y]×4] normalised 0–1, TL→TR→BR→BL — the persisted crop quad.
    quad: Array,
    // CROP on/off. Default ON: published surfaces show the straightened crop.
    enabled: { type: Boolean, default: true }
  }

  connect() {
    this._boundApply = this.apply.bind(this)

    if (this.hasImageTarget) {
      // Re-solve once the natural dimensions are known (getImgRect needs them).
      if (this.imageTarget.complete) {
        this.apply()
      } else {
        this.imageTarget.addEventListener("load", this._boundApply)
      }
    }

    // Re-solve on any layout change to the frame (responsive resize, container
    // grow/shrink). The matrix3d is pixel-dependent, so it must follow the frame.
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(this._boundApply)
      this._resizeObserver.observe(this.element)
    } else {
      window.addEventListener("resize", this._boundApply)
    }
  }

  disconnect() {
    if (this._resizeObserver) this._resizeObserver.disconnect()
    window.removeEventListener("resize", this._boundApply)
    if (this.hasImageTarget) this.imageTarget.removeEventListener("load", this._boundApply)
  }

  // Solve the homography for the current frame size and apply (or clear) it.
  apply() {
    if (!this.hasImageTarget) return
    const img = this.imageTarget

    if (!this.enabledValue) {
      this._clear()
      return
    }

    // Straighten + frame in one matrix3d (shared lib). Null while not yet
    // measurable (natural size unknown) — load/resize retries.
    const matrix = cropMatrix3d(this.element, img, this.quadValue)
    if (!matrix) {
      this._clear()
      return
    }
    // The homography is in frame-relative pixel coords — origin MUST be 0 0.
    img.style.transformOrigin = "0 0"
    img.style.transform = matrix
  }

  // CROP toggle. With an argument, set it; without, flip. Default state is ON.
  toggle(force) {
    this.enabledValue = typeof force === "boolean" ? force : !this.enabledValue
    this.apply()
  }

  enabledValueChanged() {
    // React to external toggles (e.g. a future CROP switch flipping the value).
    if (this.hasImageTarget) this.apply()
  }

  _clear() {
    if (!this.hasImageTarget) return
    this.imageTarget.style.transform = ""
    this.imageTarget.style.transformOrigin = ""
  }
}
